import { FC, useCallback, useEffect, useState } from 'react'
import { themeVars } from '@genie-fintech/ui/style/theme'
import { title } from '@genie-fintech/ui/style/typography'
import Drawer from '$components/Drawer'
import { getAppPermissionListByRoleId, TID } from '$services/api'
import { flexColumn } from '$styles/common.css'
import { drawerContent, main } from './styles.css'
import SearchBox from '$components/SearchBox'
import { useApiListingParams } from '$hooks/actions'
import { pluralize } from '$app/utils'
import { useRequest } from 'ahooks'
import Loading from '$components/Loading'
import EmptyData from '$components/EmptyData'
import Table, { Column } from '$components/Table'
import ActionLink from '$components/Table/ActionLink'
import { ROUTE_NAMES } from '$router/config'
import ScrollPagination from '$components/ScrollPagination'

const { colors } = themeVars

type Props = {
  role?: { id: TID; name: string }
  appId?: TID
  onClose: VoidFunction
}

type Permission = Awaited<
  ReturnType<typeof getAppPermissionListByRoleId>
>['data'][number]

const Block: FC<Props> = ({ role, appId }) => {
  const { id: roleId, name: roleName } = role ?? {}

  const [permissions, setPermissions] = useState<Permission[]>([])

  const {
    run: fetchAppPermissionListByRoleId,
    loading: fetchingAppPermissionListByRoleId,
    data
  } = useRequest(getAppPermissionListByRoleId, {
    manual: true,
    onSuccess: ({ data, meta: { current_page } }) => {
      setPermissions(prev => (current_page === 1 ? data : [...prev, ...data]))
    }
  })

  const { meta } = data ?? {}

  const {
    searchValue,
    updateSearchValue,
    debouncedSearchValue,
    pagerProps,
    perPage
  } = useApiListingParams({ total: meta?.total })

  const { currentPage: page, jump } = pagerProps

  useEffect(() => {
    if (!appId || !roleId) return
    fetchAppPermissionListByRoleId({
      page,
      per_page: perPage,
      application_id: appId,
      roleId,
      q: debouncedSearchValue
    })
  }, [
    appId,
    debouncedSearchValue,
    fetchAppPermissionListByRoleId,
    page,
    perPage,
    roleId
  ])

  const isEmpty = !permissions.length && !fetchingAppPermissionListByRoleId

  const hasData = !!permissions.length

  const columns: Column<Permission>[] = [
    {
      name: 'No',
      render: ({ index }) => index + 1
    },
    {
      name: 'Name',
      dataIndex: 'name'
    },
    {
      name: 'Module Name',
      dataIndex: 'module'
    },
    {
      name: '',
      render: ({ record }) => (
        <ActionLink
          to={ROUTE_NAMES.APP_PERMISSION_DETAIL}
          options={({ params }) => ({
            params: { ...params, appId, permissionId: record.id }
          })}
        />
      )
    }
  ]

  const initialLoading = page === 1 && fetchingAppPermissionListByRoleId

  return (
    <>
      <header className={flexColumn} style={{ gap: 24, flexShrink: 0 }}>
        <p className={title.two}>
          {[roleName ? `${roleName}'s` : '', 'PERMISSIONS']
            .filter(Boolean)
            .join(' ')}
        </p>

        <article className={flexColumn} style={{ gap: 16 }}>
          <SearchBox
            value={searchValue}
            onChange={updateSearchValue}
            placeholder="Search here..."
          />

          {hasData && (
            <p
              className={title.six}
              style={{
                color: colors.text.disabled,
                textTransform: 'uppercase'
              }}
            >
              {pluralize(meta?.total, 'permission')}
            </p>
          )}
        </article>
      </header>

      <main className={main}>
        {initialLoading && <Loading />}

        {!initialLoading && (
          <>
            <article>
              <Table
                data={permissions}
                loading={fetchingAppPermissionListByRoleId}
                columns={columns}
              />
            </article>

            <ScrollPagination
              loading={fetchingAppPermissionListByRoleId}
              onTrigger={jump}
              meta={meta}
            />
          </>
        )}

        {isEmpty && <EmptyData type="permission" />}
      </main>
    </>
  )
}

const PreviewPermissionDrawer = (props: Props) => {
  const { role, onClose } = props

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (open) return
      onClose()
    },
    [onClose]
  )

  return (
    <Drawer
      open={!!role?.id}
      onOpenChange={onOpenChange}
      className={drawerContent}
    >
      <Block {...props} />
    </Drawer>
  )
}

export default PreviewPermissionDrawer
